import type { ChangeEventHandler } from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Inline } from "../../Inline/Inline";
import CalendarPopover from "../CalendarPopover/CalendarPopover";
import { externalDateValidator, transformMultipleFieldDateToCalendarFormat } from "../helpers";
import type { MultipleFieldDatepickerProps } from "../types";
import FieldWithHint from "./FieldWithHint/FieldWithHint";

export const MultipleFieldDatepicker = forwardRef(
  (
    {
      disabled,
      value,
      errors = [],
      minDate,
      maxDate,
      disabledDates,
      initialCalendarType,
      buttonKind,
      onChange,
      className,
      ...fieldProps
    }: MultipleFieldDatepickerProps,
    ref
  ) => {
    const initialInnerValue = {
      day: "",
      month: "",
      year: "",
    };
    const [innerValue, setInnerValue] = useState<typeof initialInnerValue>(value);
    const dayRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setInnerValue(value);
    }, [value]);

    const onFieldChange = useCallback(
      (type: "day" | "month" | "year"): ChangeEventHandler<HTMLInputElement> =>
        (event) => {
          const { value: inputDate } = event.target;
          const newInnerValue = {
            ...innerValue,
            [type]: inputDate,
          };
          setInnerValue(newInnerValue);
          onChange(newInnerValue);
        },
      [innerValue, onChange]
    );

    useImperativeHandle(ref, () => ({
      focus() {
        dayRef.current?.focus();
      },
      get day() {
        return dayRef.current;
      },
      get month() {
        return monthRef.current;
      },
      get year() {
        return yearRef.current;
      },
    }));

    const formattedDate = transformMultipleFieldDateToCalendarFormat(value);
    const selectedCalendarDate = externalDateValidator(formattedDate) ? [formattedDate] : undefined;

    const handleCalendarChange = (date: string | undefined) => {
      if (date) {
        const [year, month, day] = date.split("-");
        const newDate = { day, month, year };
        setInnerValue(newDate);
        onChange(newDate);
      } else {
        setInnerValue(initialInnerValue);
        onChange(initialInnerValue);
      }
    };

    return (
      <Inline space="datepickerSpace" alignItems="flex-end" className={className}>
        <Inline space="sp-8">
          <FieldWithHint
            {...fieldProps}
            id={fieldProps.id ? `${fieldProps.id}-day` : ""}
            ref={dayRef}
            hasError={errors.includes("day")}
            hint="День"
            visuallyHiddenHint="ДД"
            value={innerValue.day}
            disabled={disabled}
            onChange={onFieldChange("day")}
            styles={{ width: "datepickerDayMonthFieldWidth" }}
          />
          <FieldWithHint
            {...fieldProps}
            id={fieldProps.id ? `${fieldProps.id}-month` : ""}
            ref={monthRef}
            hasError={errors.includes("month")}
            visuallyHiddenHint="ММ"
            hint="Месяц"
            value={innerValue.month}
            disabled={disabled}
            onChange={onFieldChange("month")}
            styles={{ width: "datepickerDayMonthFieldWidth" }}
          />
          <FieldWithHint
            {...fieldProps}
            id={fieldProps.id ? `${fieldProps.id}-year` : ""}
            ref={yearRef}
            hasError={errors.includes("year")}
            visuallyHiddenHint="ГГГГ"
            hint="Год"
            value={innerValue.year}
            disabled={disabled}
            onChange={onFieldChange("year")}
            styles={{ width: "datepickerYearFieldWidth" }}
          />
        </Inline>
        <CalendarPopover
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          disabledDates={disabledDates}
          buttonKind={buttonKind}
          initialCalendarType={initialCalendarType}
          selectedDate={selectedCalendarDate}
          onSelectedDateChange={handleCalendarChange}
        />
      </Inline>
    );
  }
);

MultipleFieldDatepicker.displayName = "MultipleFieldDatepicker";
